import React, { useMemo } from "react";

import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { ContainerStatus, TransportLeg, Location } from "../../../../models/marine.traffic";
import History, { Item } from "../../../Generic/History";
import useStyles from "./styles";

interface ContainerActivityProps {
  containerStatus?: ContainerStatus;
}

interface LocationLeg {
  title: string;
  locationName: string;
  date: string;
}

const ContainerActivity = ({ containerStatus }: ContainerActivityProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const getContainerActivityToHistory = () => {
    if (
      !containerStatus ||
      !containerStatus.transportationPlan ||
      !containerStatus.transportationPlan.transportLegs ||
      !containerStatus.transportationPlan.locations
    )
      return [];

    const {
      transportationPlan: { transportLegs, locations },
    } = containerStatus;

    const locationLegs: LocationLeg[] = [];
    const activities: Item[] = [];

    if (transportLegs.length > 0 && locations.length > 0) {
      // Each transport leg in a status container has the arrival and departure data of that connection,
      // the transport legs array comes unorganized and it is necessary to link the portId to the
      // locations array to find the location of the port used
      transportLegs.forEach((transportLeg: TransportLeg) => {
        // Departure
        if (transportLeg.departureLocation && transportLeg.portActualDepartureUtc) {
          const location = locations.find(
            (location: Location) => location.portId === transportLeg.departureLocation.portId
          );

          if (!location) return;

          locationLegs.push({
            title: translate("departureAtPort"),
            locationName: location.locationName,
            date: transportLeg.portActualDepartureUtc,
          });
        }

        // Arrival
        if (transportLeg.arrivalLocation && transportLeg.portActualArrivalUtc) {
          const location = locations.find(
            (location: Location) => location.portId === transportLeg.arrivalLocation.portId
          );

          if (!location) return;

          locationLegs.push({
            title: translate("arrivalAtPort"),
            locationName: location.locationName,
            date: transportLeg.portActualArrivalUtc,
          });
        }
      });
    }

    // Order location legs by date in descending order
    locationLegs.sort((currentLocationLeg: LocationLeg, nextLocationLeg: LocationLeg) => {
      return new Date(nextLocationLeg.date).getTime() - new Date(currentLocationLeg.date).getTime();
    });

    // Creates the history array with locationName and date concatenated
    locationLegs.forEach((activity) => {
      activities.push({
        title: activity.title,
        activityDate: `${activity.locationName} - ${format(
          new Date(activity.date),
          "dd/MM/yyyy hh:mm aa"
        )}`,
      } as Item);
    });

    return activities;
  };

  const activityMemoPanel = useMemo(
    () => <History items={getContainerActivityToHistory()} />,
    [containerStatus]
  );

  return <div className={classes.activityDiv}>{activityMemoPanel}</div>;
};

export default ContainerActivity;
